import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LoadingContainer, ButtonSpinner } from '../components/ui/Spinner';
import { PlusIcon, MapPinIcon } from '@heroicons/react/24/outline';

// Game quality color mapping
const GAME_QUALITY_COLORS = {
    excellent: 'border-l-green-500',
    good: 'border-l-blue-500',
    average: 'border-l-gray-400',
    tough: 'border-l-orange-500',
    very_tough: 'border-l-red-500',
    '': 'border-l-gray-300 dark:border-l-gray-600',
};

const GAME_QUALITY_LABELS = {
    excellent: 'Excellent',
    good: 'Good',
    average: 'Average',
    tough: 'Tough',
    very_tough: 'Very Tough',
};

const GAME_QUALITY_BADGE_COLORS = {
    excellent: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    good: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    average: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    tough: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
    very_tough: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};

// Reusable Input Component
const Input = ({ label, type = "text", value, onChange, required = false, className = "", ...props }) => (
    <div className={`relative ${className}`}>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {label} {required && <span className="text-red-500">*</span>}
        </label>
        <input
            type={type}
            value={value}
            onChange={onChange}
            required={required}
            className="block w-full rounded-lg border border-gray-300 dark:border-gray-600
                     bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white
                     focus:border-primary-500 dark:focus:border-primary-400
                     focus:outline-none focus:ring-1 focus:ring-primary-500 dark:focus:ring-primary-400
                     disabled:opacity-50 disabled:cursor-not-allowed"
            {...props}
        />
    </div>
);

function LocationsPage() {
    const navigate = useNavigate();
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState(null);
    const [newLocation, setNewLocation] = useState({ name: '', city: '', state: '' });

    const fetchLocations = () => {
        setLoading(true);
        axios.get('/api/locations/')
            .then(res => {
                setLocations(res.data || []);
                setLoading(false);
            })
            .catch(err => {
                setError(err.response?.data?.detail || 'Failed to fetch locations.');
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchLocations();
    }, []);

    const handleSubmit = (e) => {
        e.preventDefault();
        setFormLoading(true);
        setFormError(null);

        axios.post('/api/locations/', newLocation)
            .then(() => {
                setNewLocation({ name: '', city: '', state: '' });
                setShowAddForm(false);
                fetchLocations();
            })
            .catch(err => {
                setFormError(err.response?.data?.detail || err.response?.data?.name?.[0] || 'Failed to create location.');
            })
            .finally(() => {
                setFormLoading(false);
            });
    };

    if (loading) {
        return <LoadingContainer message="Loading locations..." />;
    }

    if (error) {
        return (
            <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/30
                          text-red-700 dark:text-red-400 m-4">
                {error}
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto mt-8 mb-8 px-2 sm:px-4">
            <div className="bg-white dark:bg-gray-800 border border-gray-200
                          dark:border-gray-700 rounded-2xl p-4 sm:p-6 mb-6
                          shadow-sm relative">
                <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <MapPinIcon className="h-8 w-8 text-primary-600" />
                        Locations
                    </h1>
                    <button
                        onClick={() => setShowAddForm(!showAddForm)}
                        className="p-2 sm:p-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700
                                 shadow-sm hover:shadow transition-all duration-200
                                 transform hover:scale-105 focus:outline-none focus:ring-2
                                 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                        title="Add Location"
                    >
                        <PlusIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                    </button>
                </div>

                {/* Add Location Form */}
                {showAddForm && (
                    <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Add New Location</h3>
                        <form onSubmit={handleSubmit}>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                                <Input
                                    label="Name"
                                    value={newLocation.name}
                                    onChange={(e) => setNewLocation({ ...newLocation, name: e.target.value })}
                                    required
                                    placeholder="e.g., Hard Rock Hollywood"
                                />
                                <Input
                                    label="City"
                                    value={newLocation.city}
                                    onChange={(e) => setNewLocation({ ...newLocation, city: e.target.value })}
                                    placeholder="e.g., Hollywood"
                                />
                                <Input
                                    label="State"
                                    value={newLocation.state}
                                    onChange={(e) => setNewLocation({ ...newLocation, state: e.target.value })}
                                    placeholder="e.g., FL"
                                />
                            </div>
                            {formError && (
                                <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-sm">
                                    {formError}
                                </div>
                            )}
                            <div className="flex gap-2">
                                <button
                                    type="submit"
                                    disabled={formLoading}
                                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700
                                             disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    {formLoading && <ButtonSpinner />}
                                    Add Location
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowAddForm(false)}
                                    className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-white
                                             rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Locations Grid */}
                {locations.length === 0 ? (
                    <div className="text-center py-12">
                        <MapPinIcon className="h-16 w-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                        <p className="text-gray-500 dark:text-gray-400 text-lg">No locations yet.</p>
                        <p className="text-gray-400 dark:text-gray-500 text-sm mt-2">
                            Click the + button to add your first poker room.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {locations.map(location => (
                            <div
                                key={location.id}
                                onClick={() => navigate(`/locations/${location.id}`)}
                                className={`bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600
                                          rounded-xl p-4 cursor-pointer hover:shadow-md transition-all duration-200
                                          border-l-4 ${GAME_QUALITY_COLORS[location.typical_game_quality] || GAME_QUALITY_COLORS['']}`}
                            >
                                <div className="flex justify-between items-start mb-2">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                        {location.name}
                                    </h3>
                                    {location.typical_game_quality && (
                                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${GAME_QUALITY_BADGE_COLORS[location.typical_game_quality]}`}>
                                            {GAME_QUALITY_LABELS[location.typical_game_quality]}
                                        </span>
                                    )}
                                </div>

                                {(location.city || location.state) && (
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                                        {[location.city, location.state].filter(Boolean).join(', ')}
                                    </p>
                                )}

                                {location.stakes_offered && (
                                    <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">
                                        Stakes: {location.stakes_offered}
                                    </p>
                                )}

                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-500 dark:text-gray-400">
                                        {location.session_count || 0} sessions
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default LocationsPage;
